import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { keyframes } from "@mui/system";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";

const marquee = (distance) => keyframes`
    from { transform: translate3d(0,0,0); }
    to   { transform: translate3d(-${distance}px, 0, 0); }
`;

export function InstagramStyleMarquee({
                                          items = [],
                                          profileUrl = "",
                                          gap = 16,
                                          pixelsPerSecond = 50,
                                          pauseOnHover = true,
                                          showTypeIcon = true,
                                          sx,
                                      }) {
    const canHover = useMediaQuery("(hover: hover) and (pointer: fine)");
    const viewportRef = useRef(null);
    const measureRef = useRef(null);

    const [sequenceWidth, setSequenceWidth] = useState(0);
    const [viewportWidth, setViewportWidth] = useState(0);

    const baseItems = useMemo(() => {
        const seen = new Set();
        return (items || []).reduce((acc, it, idx) => {
            if (!it?.src || seen.has(it.src)) return acc;
            seen.add(it.src);
            acc.push({
                id: it.id || `${it.src}-${idx}`,
                type: it.type === "video" ? "video" : "image",
                src: it.src,
            });
            return acc;
        }, []);
    }, [items]);

    // 🔹 Only effect: measure widths
    useEffect(() => {
        const viewportEl = viewportRef.current;
        const measureEl = measureRef.current;

        if (!viewportEl || !measureEl) return;

        let rafId = 0;

        const update = () => {
            // Guard in case observer fires after unmount/reconcile
            if (!viewportEl.isConnected || !measureEl.isConnected) return;

            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                setViewportWidth(viewportEl.offsetWidth || 0);
                setSequenceWidth(measureEl.offsetWidth || 0);
            });
        };

        update();

        const ro = new ResizeObserver(() => update());
        ro.observe(viewportEl);
        ro.observe(measureEl);

        return () => {
            cancelAnimationFrame(rafId);
            ro.disconnect();
        };
    }, [baseItems, gap]);

    if (!baseItems.length) return null;

    const repeats =
        sequenceWidth > 0 && viewportWidth > 0
            ? Math.max(2, Math.ceil(viewportWidth / sequenceWidth) + 2)
            : 3;

    const duration =
        sequenceWidth > 0 ? Math.max(10, sequenceWidth / pixelsPerSecond) : 20;

    return (
        <Box sx={{ width: "100%", ...sx }}>
            <Box ref={viewportRef} sx={{ overflow: "hidden", py: 1 }}>
                <Box
                    sx={{
                        display: "flex",
                        width: "max-content",
                        gap: `${gap}px`,
                        willChange: "transform",
                        animation:
                            sequenceWidth > 0
                                ? `${marquee(sequenceWidth + gap)} ${duration}s linear infinite`
                                : "none",

                        // ✅ pause ONLY on real hover devices
                        ...(pauseOnHover &&
                            canHover && {
                                "&:hover": { animationPlayState: "paused" },
                            }),
                    }}
                >
                    {/* Hidden measurer (one full sequence) */}
                    <Box
                        ref={measureRef}
                        sx={{
                            position: "absolute",
                            visibility: "hidden",
                            display: "flex",
                            gap: `${gap}px`,
                        }}
                    >
                        <InstagramRow
                            items={baseItems}
                            profileUrl={profileUrl}
                            showTypeIcon={showTypeIcon}
                            canHover={canHover}
                        />
                    </Box>

                    {/* Render repeated rows */}
                    {Array.from({ length: repeats }).map((_, i) => (
                        <InstagramRow
                            key={i}
                            items={baseItems}
                            profileUrl={profileUrl}
                            showTypeIcon={showTypeIcon}
                            canHover={canHover}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

function InstagramRow({ items, profileUrl, showTypeIcon, canHover }){
    return (
        <>
            {items.map((item) => (
                <InstagramThumbCard
                    key={item.id}
                    src={item.src}
                    type={item.type}
                    href={profileUrl}
                    showTypeIcon={showTypeIcon}
                    canHover={canHover}
                />
            ))}
        </>
    );
}

function InstagramThumbCard({ src, type, href, showTypeIcon, canHover }) {
    const [loaded, setLoaded] = useState(false);

    return (
        <Box
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
                flex: "0 0 auto",
                width: { xs: 160, sm: 180, md: 200, lg: 220, xl: 240 },
                aspectRatio: "4 / 5",
                borderRadius: 3,
                overflow: "hidden",
                position: "relative",
                textDecoration: "none",
                backgroundColor: "rgba(0,0,0,0.04)",
                boxShadow: "0 1px 10px rgba(0,0,0,0.10)",
                transform: "translateY(0px) scale(1)",
                transition: "transform 180ms ease, box-shadow 180ms ease",
                ...(canHover && {
                    "&:hover": {
                    transform: "translateY(-6px) scale(1.02)",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.18)",
                    zIndex: 2,
                    },
                }),
            }}
        >
            {!loaded && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "linear-gradient(110deg, #f0f0f0 8%, #f7f7f7 18%, #f0f0f0 33%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.2s linear infinite",
                    }}
                />
            )}

            <Box
                component="img"
                src={src}
                alt=""
                loading="lazy"
                onLoad={() => setLoaded(true)}
                sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: loaded ? 1 : 0,
                    transition: "opacity 300ms ease",
                }}
            />

            {showTypeIcon && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        backgroundColor: "rgba(0,0,0,0.55)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        pointerEvents: "none",
                    }}
                >
                    {type === "video" ? (
                        <PlayArrowRoundedIcon fontSize="small" />
                    ) : (
                        <ImageRoundedIcon fontSize="small" />
                    )}
                </Box>
            )}
        </Box>
    );
}
